// ** MUI Imports
import React from 'react';
import { Typography, CardContent, CardHeader, CircularProgress, Container } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { useEffect, useState, ComponentType, ReactElement } from 'react'
import axios from 'src/services/ApiService'
import FBEngagementChart from '../components/InsightsComponents/FBinsightChart'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button'
import { Box, useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import ListItemIcon from '@mui/material/ListItemIcon';
import { SvgIconProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import Google from 'mdi-material-ui/Google';
import Facebook from 'mdi-material-ui/Facebook';
import Pinterest from 'mdi-material-ui/Pinterest';
import Instagram from 'mdi-material-ui/Instagram';
import InstagramUserInsights from '../components/InsightsComponents/InstagramUserInsights';
import FbPageInsights from '../components/InsightsComponents/FbPageInsights';
import InstagramInsightChart from '../components/InsightsComponents/InstagramInsightChart';
import FBPostInsights from '../components/InsightsComponents/FbPostInsights';
import InstagramPostInsights from '../components/InsightsComponents/InstagramPostInsights';


interface ValueItem {
    value: object;
    numberValue: number;
    decimalValue: number;
}

interface DataItem {
    name: string;
    period: string;
    values: ValueItem[];
    title: string;
    description: string;
    id: string;
}

interface FBInsightsDTO {
    data: DataItem[];
    metricsProfile: EngagementData[];
    postWiseInsights: PostInsights[];
    instagramPostInsightsMetrics: InstagramPostMetrics[];
}

interface PostInsights {
    post_impressions: number;
    post_impressions_unique: number;
    post_clicks: number;
    reactions: number;
    created_time: string;
    message: string;
    id: string;
    engagementRate: string;
    full_picture: string;
    likes: string;
    comments: string;
}

interface InstagramPostMetrics {
    timestamp: string;
    caption: string;
    id: string;
    engagementRate: string;
    media_url: string;
    likes: string;
    comments: string;
    follows: string;
    profile_activity: string;
    reach: string;
    total_interactions: string;
    impressions: string;
    reactions: number;
}

interface EngagementData {
    date: string;
    name: string;
    value: number;
}


const Insights = () => {
    const [channels, setChannels] = useState<any>(null);
    const [selectedChannel, setSelectedChannel] = useState<string>('');
    const [insightsData, setInsightsData] = useState<FBInsightsDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [channelMediumName, setChannelMediumName] = useState<string>('');
    const [selectedTimeFrame, setSelectedTimeFrame] = useState<number>(0);

    const theme = useTheme();
    const handleChange = async (e: any) => {
        const selectedValue = e.target.value;
        setSelectedChannel(selectedValue);
    };

    const getData = async () => {

        if (!selectedChannel) {
            alert("Please select a channel");
            return;
        }
        if ((!startDate && endDate) || (startDate && !endDate)) {
            alert("Please select both Start Date and End Date");
            return;
        }
        if (startDate && endDate && startDate > endDate) {
            alert("Start Date cannot be after End Date");
            return;
        }
        if (startDate && endDate && channelMediumName === "Instagram" && selectedTimeFrame > 30) {
            const err = new Error("Instagram data range limited to 30 days. Please adjust dates");
            setError(err);
            return;
        }
        setLoading(true);
        setError(null);
        setInsightsData(null);

        try {
            const response = await axios.get<FBInsightsDTO>(`insight/GetPageInsights?channelGuid=${selectedChannel}&startDate=${startDate!.toDateString()}&endDate=${endDate!.toDateString()}`);
            setInsightsData(response.data);
        } catch (error: any) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }

    const fetchData = async () => {
        try {
            const response = await axios.get('insight/GetChannels');
            setChannels(response.data);
        } catch (error) {

        } finally {

        }
    };

    const styledIcon = <T extends ComponentType<SvgIconProps>>(IconComponent: T) => styled(IconComponent)(({ theme }) => ({
        fontSize: '15px',
    }));

    const getChannelName = (mediumName: any) => {
        setChannelMediumName(mediumName);
    }

    const GetIconForChannel = (mediumName: string): ReactElement | null => {
        let IconComponent: ComponentType<SvgIconProps> | null = null;

        if (mediumName === "Google") {
            IconComponent = styledIcon(Google);
        } else if (mediumName === "Facebook") {
            IconComponent = styledIcon(Facebook);
        } else if (mediumName === "Pinterest") {
            IconComponent = styledIcon(Pinterest);
        } else if (mediumName === "Instagram") {
            IconComponent = styledIcon(Instagram);
        }

        return IconComponent ? React.createElement(IconComponent) : null;
    };

    useEffect(() => {
        if (startDate !== null && endDate !== null) {
            const startDateString = startDate!.toDateString();
            const endDateString = endDate!.toDateString();
            const selectedStartDate = new Date(startDateString);
            const selectedEndDate = new Date(endDateString);
            const differenceInMilliseconds = selectedEndDate.getTime() - selectedStartDate.getTime();
            const timeFrame = Math.round(differenceInMilliseconds / (1000 * 3600 * 24));
            setSelectedTimeFrame(timeFrame);
        }
    }, [startDate, endDate])

    useEffect(() => {
        fetchData();
    }, [])

    return (<>
        {(
            <Container maxWidth={"xl"}>
                <Typography variant='h4' sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Insights
                </Typography>
                <Typography variant='body2' sx={{ mb: 4 }}>
                    View your social media channels insights
                </Typography>
                <Card sx={{ backgroundColor: theme.palette.background.paper , margin: '0 auto', mb: 2 }}>
                    <CardHeader title={insightsData && insightsData.data.length > 0 ? "Insights Data" : "Select Channel & Date Range"}>
                    </CardHeader>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <FormControl variant='filled' fullWidth sx={{ minHeight: '40px' }}>
                                    <Select
                                        label='Channel'
                                        defaultValue=''
                                        onChange={handleChange}
                                        value={selectedChannel}
                                        sx={{ fontSize: '0.8rem', minHeight: '30px' }}
                                    >
                                        {channels && channels.map((c: any) => (
                                            <MenuItem value={c.channelGuid} key={c.channelGuid} onClick={() => getChannelName(c.mediumName)}>
                                                <ListItemIcon >
                                                    {c.name}
                                                    <Badge overlap="circular" badgeContent={GetIconForChannel(c.mediumName)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} sx={{ marginLeft: '14px' }} color='primary'>
                                                    </Badge>
                                                </ListItemIcon>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Grid item xs={12} sm={3}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(date) => setStartDate(new Date(date?.toString() || ''))}
                                        renderInput={(params) => <TextField {...params} variant="outlined" fullWidth size="small" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={(date) => setEndDate(new Date(date?.toString() || ''))}
                                        renderInput={(params) => <TextField {...params} variant="outlined" fullWidth size="small" />}
                                    />
                                </Grid>
                            </LocalizationProvider>
                            <Grid item xs={12} sm={3}>
                                <Button variant='outlined' onClick={() => getData()} fullWidth disabled={loading}>
                                    Filter
                                    {loading && <CircularProgress size={24} sx={{ marginLeft: 1 }} />}
                                </Button>
                            </Grid>
                        </Grid>
                        {error && <Typography variant="body1" style={{ color: '#FF4560', marginTop: '10px' }}>
                            Error: {error.message}
                        </Typography>}
                        {insightsData && insightsData.data.length > 0 &&
                            <InstagramUserInsights insightsData={insightsData.data} />
                            /*<FbPageInsights insightsData={insightsData.data} />*/ // Facebook insights
                        }
                    </CardContent>
                </Card>
            </Container>
        )}



        {channelMediumName === "Facebook" && insightsData && insightsData.metricsProfile && insightsData.metricsProfile.length > 0 &&
            <FBEngagementChart
                data={insightsData.metricsProfile}
            />
        }
        {channelMediumName === "Instagram" && insightsData && insightsData.metricsProfile && insightsData.metricsProfile.length > 0 &&
            <InstagramInsightChart data={insightsData.metricsProfile} />
        }

        {
            channelMediumName === "Facebook" && insightsData && insightsData.postWiseInsights && insightsData.postWiseInsights.length > 0 &&
            <FBPostInsights postWiseInsights={insightsData.postWiseInsights} />
        }
        {
            channelMediumName === "Instagram" && insightsData && insightsData.instagramPostInsightsMetrics && insightsData.instagramPostInsightsMetrics.length > 0 &&
            <InstagramPostInsights InstagramPostInsightsMetrics={insightsData.instagramPostInsightsMetrics} />
        }
    </>
    )
}

export default Insights